// Reservation writes update the cache immediately; the durable event stream
// refreshes the shared prefix once. Calendar operations remain dormant.
import {
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addDays,
  type CaseDayAction,
  type CaseStatus,
  planFields,
} from "@trameria/core/cases-shared";
import {
  BLOCK_MIN_MINUTES,
  type CalendarEvent,
  type CalendarEvents,
  type CalendarLane,
  type CalendarSettings,
  type CalendarSource,
  type GoogleConnection,
  type LinkTarget,
  type TimeBlock,
  type TimeBlockerDay,
  type TimeBlockerNow,
  type TimeBlockerReview,
} from "@trameria/core/timeblocker-shared";
import { useApi } from "../app/context";
import { dateIn } from "../cases/dates";
import { caseChanged } from "../cases/queries";
import type { Api } from "../data/api";
import { idempotencyKeyFor } from "../data/api";
import { newOptimisticId } from "../data/optimistic";
import { dayRange } from "./day";
import { applyEventBody, type EventTimesBody } from "./event-time";
import { type BlockHistory, historyOf } from "./history";
import { startedWorks, stoppedWorks } from "./works";

// ---- keys and reads ------------------------------------------------------------

export const timeBlockerPrefix = ["me", "timeblocker"] as const;
export const dayPrefix = [...timeBlockerPrefix, "day"] as const;
export const runningKey = [...timeBlockerPrefix, "now"] as const;
export const calendarPrefix = ["me", "calendar"] as const;
const calendarSettingsKey = [...calendarPrefix, "settings"] as const;
const eventsPrefix = [...calendarPrefix, "events"] as const;
export const googleKey = ["me", "google"] as const;

const dayKey = (day: string, timeZone: string | undefined, actions = true) =>
  [...dayPrefix, day, timeZone ?? "", actions] as const;
const eventsKey = (day: string, timeZone: string | undefined) =>
  [...eventsPrefix, day, timeZone ?? ""] as const;

const rangeQuery = (day: string, timeZone: string | undefined) => {
  const { from, to } = dayRange(day, timeZone);
  return `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
};

export const dayQuery = (
  api: Api,
  day: string,
  timeZone: string | undefined,
  actions = true,
) =>
  queryOptions({
    staleTime: Number.POSITIVE_INFINITY,
    queryKey: dayKey(day, timeZone, actions),
    queryFn: () =>
      api<TimeBlockerDay>(
        `/api/me/timeblocker/day?on=${day}&${rangeQuery(day, timeZone)}${actions ? "" : "&actions=0"}`,
      ),
  });

export const nowQuery = (api: Api) =>
  queryOptions({
    staleTime: Number.POSITIVE_INFINITY,
    queryKey: runningKey,
    queryFn: () => api<TimeBlockerNow>("/api/me/timeblocker/now"),
  });

export const reviewPrefix = [...timeBlockerPrefix, "review"] as const;

/** A week's review: Monday 0:00 in the time zone, 7 days, one request. */
export const reviewQuery = (
  api: Api,
  weekStart: string,
  timeZone: string | undefined,
) =>
  queryOptions({
    staleTime: Number.POSITIVE_INFINITY,
    queryKey: [...reviewPrefix, weekStart, timeZone ?? ""] as const,
    queryFn: () => {
      const from = dayRange(weekStart, timeZone).from,
        to = dayRange(addDays(weekStart, 6), timeZone).to;
      return api<TimeBlockerReview>(
        `/api/me/timeblocker/review?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${timeZone ? `&tz=${encodeURIComponent(timeZone)}` : ""}`,
      );
    },
  });

export const calendarSettingsQuery = (api: Api) =>
  queryOptions({
    queryKey: calendarSettingsKey,
    queryFn: () => api<CalendarSettings>("/api/me/calendar"),
  });

export const calendarEventsQuery = (
  api: Api,
  day: string,
  timeZone: string | undefined,
) =>
  queryOptions({
    queryKey: eventsKey(day, timeZone),
    queryFn: () =>
      api<CalendarEvents>(
        `/api/me/calendar/events?${rangeQuery(day, timeZone)}`,
      ),
    staleTime: 60_000,
  });

export interface GoogleStatus {
  available: boolean;
  connections: GoogleConnection[];
}

export const googleQuery = (api: Api) =>
  queryOptions({
    queryKey: googleKey,
    queryFn: () => api<GoogleStatus>("/api/me/google"),
  });

// ---- realtime ------------------------------------------------------------------

/** A block, a work log or an Action changed: the days and the running work. */
export function timeBlockerChanged(qc: QueryClient) {
  void qc.invalidateQueries(
    { queryKey: timeBlockerPrefix },
    { cancelRefetch: false },
  );
}

/** Lanes, calendars or Google accounts changed. */
export function calendarChanged(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: calendarPrefix });
  void qc.invalidateQueries({ queryKey: googleKey });
}

// ---- reservations and Actions -------------------------------------------------
export interface DayPlace {
  day: string;
  timeZone: string | undefined;
}
const _keyOf = (p: DayPlace) => dayKey(p.day, p.timeZone);
export interface AddBlockInput extends DayPlace {
  startAt: string;
  endAt: string;
  title?: string | null;
  links?: LinkTarget[];
}
const targetBody = (t: LinkTarget) => ({
  case_id: t.caseId,
  ...(t.stepId ? { step_id: t.stepId } : {}),
});
const blockPath = (id: string) =>
  `/api/me/timeblocker/blocks/${encodeURIComponent(id)}`;
type Options = {
  onError?(e: Error): void;
  onSuccess?(): void;
};
type BlockFields = Partial<Pick<TimeBlock, "startAt" | "endAt" | "title">>;
/** A Block as a new one: its times, title and links (not its running state). */
const createWrite = (place: DayPlace, b: TimeBlock): BlockWrite => ({
  place,
  method: "POST",
  body: {
    start_at: b.startAt,
    end_at: b.endAt,
    plan_on: dateIn(b.startAt, place.timeZone),
    title: b.title,
    links: b.links.map(targetBody),
  },
  draft: {
    ...b,
    id: newOptimisticId(),
    startedAt: null,
    endedAt: null,
    committedStartAt: null,
    committedEndAt: null,
  },
});
const linkWrite = (
  place: DayPlace,
  blockId: string,
  target: LinkTarget,
  remove: boolean,
): BlockWrite => ({
  place,
  id: blockId,
  method: remove ? "DELETE" : "POST",
  suffix: remove
    ? `/links?${new URLSearchParams(targetBody(target))}`
    : "/links",
  body: remove ? undefined : { ...targetBody(target), plan_on: place.day },
});
/** The plans the Actions among `targets` have now (read from the days on screen; unknown ones are left out). */
function plansOf(qc: QueryClient, targets: LinkTarget[]): PlanSnapshot[] {
  const actions = qc
    .getQueriesData<TimeBlockerDay>({ queryKey: dayPrefix })
    .flatMap(([, data]) => data?.actions ?? []);
  const out: PlanSnapshot[] = [];
  for (const t of targets) {
    const a = t.stepId ? actions.find((x) => x.id === t.stepId) : undefined;
    if (!a) continue;
    out.push({
      caseId: a.caseId,
      stepId: a.id,
      plan: a.someday
        ? "someday"
        : a.plan
          ? { scope: a.plan.scope, on: a.plan.on }
          : null,
    });
  }
  return out;
}
/** What a recorded write hands to its `record`: the history and a way to write without recording. */
interface Recorder {
  history: BlockHistory;
  run(write: BlockWrite): Promise<TimeBlock | undefined>;
  /** Puts Actions' plans back to what they were (best effort: a refusal leaves them). */
  restorePlans(plans: PlanSnapshot[]): Promise<void>;
}
interface PlanSnapshot {
  caseId: string;
  stepId: string;
  plan: ActionPatch["plan"];
}
type BlockWrite = {
  place: DayPlace;
  id?: string;
  method: "POST" | "PATCH" | "DELETE";
  suffix?: string;
  body?: unknown;
  draft?: TimeBlock;
  patch?: Partial<TimeBlock>;
  /** Called when the server accepted it (also after the component is gone): records it for ⌘Z. */
  record?(r: Recorder, block: TimeBlock | undefined): void;
};
function useBlockWrite() {
  const api = useApi(),
    qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (i: BlockWrite) =>
      api<TimeBlock | undefined>(
        `${i.id ? blockPath(i.id) : "/api/me/timeblocker/blocks"}${i.suffix ?? ""}`,
        {
          method: i.method,
          body: i.body,
          ...(i.method === "POST"
            ? { idempotencyKey: idempotencyKeyFor(i) }
            : {}),
        },
      ),
    onMutate: async (i) => {
      // An undo waits for the writes it would otherwise run ahead of.
      if (i.record) historyOf(qc).begin();
      await qc.cancelQueries({ queryKey: timeBlockerPrefix });
      const snapshot = qc.getQueriesData({ queryKey: timeBlockerPrefix });
      for (const [key, value] of qc.getQueriesData<TimeBlockerDay>({
        queryKey: dayPrefix,
      })) {
        if (!value) continue;
        const range = dayRange(
          String(key[3]),
          typeof key[4] === "string" && key[4] ? key[4] : undefined,
        );
        const overlaps = (b: TimeBlock) =>
          b.startAt < range.to && b.endAt > range.from;
        const blocks = i.draft
          ? overlaps(i.draft)
            ? [...value.blocks, i.draft]
            : value.blocks
          : i.method === "DELETE" && !i.suffix
            ? value.blocks.filter((b) => b.id !== i.id)
            : value.blocks
                .map((b) => (b.id === i.id ? { ...b, ...i.patch } : b))
                .filter(overlaps);
        qc.setQueryData(key, { ...value, blocks });
      }
      const runsNow =
        !!i.patch?.startedAt ||
        i.patch?.endedAt === null ||
        i.patch?.works !== undefined;
      const stops = !!i.patch?.endedAt || (i.method === "DELETE" && !i.suffix);
      if (runsNow || stops)
        qc.setQueryData<TimeBlockerNow>(runningKey, (old) => {
          if (!old) return old;
          const block = runsNow
            ? qc
                .getQueriesData<TimeBlockerDay>({ queryKey: dayPrefix })
                .flatMap(([, data]) => data?.blocks ?? [])
                .find((b) => b.id === i.id)
            : null;
          return {
            ...old,
            running: block ?? (old.running?.id === i.id ? null : old.running),
          };
        });
      return { snapshot };
    },
    onSuccess: (block, i) => {
      i.record?.(
        {
          history: historyOf(qc),
          run: (w) => mutation.mutateAsync(w),
          restorePlans: async (plans) => {
            for (const p of plans) {
              await api(`/api/me/cases/${encodeURIComponent(p.caseId)}`, {
                method: "PATCH",
                body: { update_steps: [{ id: p.stepId, plan: p.plan }] },
              }).catch(() => {});
              caseChanged(qc, p.caseId);
            }
          },
        },
        block,
      );
      if (!block) return;
      // The running Block as the server has it (what is worked on inside it too).
      qc.setQueryData<TimeBlockerNow>(runningKey, (old) =>
        old?.running?.id === block.id && !block.endedAt
          ? { ...old, running: block }
          : old,
      );
      qc.setQueriesData<TimeBlockerDay>({ queryKey: dayPrefix }, (old) =>
        old
          ? {
              ...old,
              blocks: old.blocks.map((b) =>
                b.id === (i.draft?.id ?? i.id) ? block : b,
              ),
            }
          : old,
      );
    },
    onError: (_e, _i, ctx) => {
      for (const [key, value] of ctx?.snapshot ?? [])
        qc.setQueryData(key, value);
    },
    onSettled: (_d, _e, i) => {
      if (i.record) historyOf(qc).end();
    },
    // Successful writes are refreshed by timeblocker.changed, including reconnect replay.
  });
  return mutation;
}
/**
 * The place a new block takes, kept off the blocks there already (they do not
 * overlap): moved to the end of the block its start falls in, and cut where the
 * next one starts. As asked when it cannot fit 15 minutes (the server then says no).
 */
function fitFree(qc: QueryClient, startAt: string, endAt: string) {
  const known = new Map<string, TimeBlock>();
  for (const [, data] of qc.getQueriesData<TimeBlockerDay>({
    queryKey: dayPrefix,
  }))
    for (const b of data?.blocks ?? []) known.set(b.id, b);
  let s = Date.parse(startAt),
    e = Date.parse(endAt);
  const length = e - s;
  for (const b of [...known.values()].sort((a, c) =>
    a.startAt.localeCompare(c.startAt),
  )) {
    const bs = Date.parse(b.startAt),
      be = Date.parse(b.endAt);
    if (bs >= e || be <= s) continue;
    if (bs <= s) {
      s = be;
      e = s + length;
    } else e = bs;
  }
  return e - s >= BLOCK_MIN_MINUTES * 60_000
    ? { startAt: new Date(s).toISOString(), endAt: new Date(e).toISOString() }
    : { startAt, endAt };
}
export function useAddBlock() {
  const qc = useQueryClient();
  const write = useBlockWrite();
  return {
    ...write,
    mutate: (
      input: AddBlockInput,
      options?: {
        onError?(e: Error): void;
        onSuccess?(block: TimeBlock | undefined): void;
      },
    ) => {
      const i = { ...input, ...fitFree(qc, input.startAt, input.endAt) };
      const plans = plansOf(qc, i.links ?? []);
      const place = { day: i.day, timeZone: i.timeZone };
      write.mutate(
        {
          place: i,
          method: "POST",
          body: {
            start_at: i.startAt,
            end_at: i.endAt,
            plan_on: i.day,
            title: i.title,
            links: i.links?.map(targetBody),
          },
          draft: {
            id: newOptimisticId(),
            title: i.title ?? null,
            startAt: i.startAt,
            endAt: i.endAt,
            startedAt: null,
            endedAt: null,
            committedStartAt: null,
            committedEndAt: null,
            links: [],
            works: [],
          },
          record: (r, block) => {
            if (!block) return;
            r.history.push({
              blocks: [block.id],
              undo: async () => {
                await r.run({
                  place,
                  id: r.history.resolve(block.id),
                  method: "DELETE",
                });
                await r.restorePlans(plans);
              },
              redo: async () => {
                const again = await r.run(createWrite(place, block));
                if (again) r.history.replaced(block.id, again.id);
              },
            });
          },
        },
        options,
      );
    },
  };
}
export function useMoveBlock() {
  const write = useBlockWrite();
  return {
    ...write,
    mutate: (
      i: DayPlace & {
        block: TimeBlock;
        startAt?: string;
        endAt?: string;
        title?: string | null;
      },
      options?: Options,
    ) => {
      const was: BlockFields = {
        ...(i.startAt ? { startAt: i.block.startAt } : {}),
        ...(i.endAt ? { endAt: i.block.endAt } : {}),
        ...(i.title !== undefined ? { title: i.block.title } : {}),
      };
      const now: BlockFields = {
        ...(i.startAt ? { startAt: i.startAt } : {}),
        ...(i.endAt ? { endAt: i.endAt } : {}),
        ...(i.title !== undefined ? { title: i.title } : {}),
      };
      const put = (
        id: string,
        to: BlockFields,
        record?: BlockWrite["record"],
      ): BlockWrite => ({
        place: i,
        id,
        method: "PATCH",
        body: { start_at: to.startAt, end_at: to.endAt, title: to.title },
        patch: to,
        record,
      });
      write.mutate(
        put(i.block.id, now, (r) =>
          r.history.push({
            blocks: [i.block.id],
            undo: async () => {
              await r.run(put(r.history.resolve(i.block.id), was));
            },
            redo: async () => {
              await r.run(put(r.history.resolve(i.block.id), now));
            },
          }),
        ),
        options,
      );
    },
  };
}
export function useRemoveBlock() {
  const qc = useQueryClient();
  const write = useBlockWrite();
  return {
    ...write,
    mutate: (i: DayPlace & { block: TimeBlock }, options?: Options) => {
      const place = { day: i.day, timeZone: i.timeZone };
      const removed = i.block;
      const plans = plansOf(qc, removed.links);
      write.mutate(
        {
          place: i,
          id: removed.id,
          method: "DELETE",
          record: (r) =>
            r.history.push({
              blocks: [removed.id],
              undo: async () => {
                const back = await r.run(createWrite(place, removed));
                if (back) r.history.replaced(removed.id, back.id);
                await r.restorePlans(plans);
              },
              redo: async () => {
                await r.run({
                  place,
                  id: r.history.resolve(removed.id),
                  method: "DELETE",
                });
              },
            }),
        },
        options,
      );
    },
  };
}
/** `untracked`: the screen is not the TimeBlocker's (its writes stay out of its ⌘Z). */
export function useLinkBlock({ untracked = false } = {}) {
  const qc = useQueryClient();
  const write = useBlockWrite();
  return {
    ...write,
    mutate: (
      i: DayPlace & { blockId: string; target: LinkTarget; remove?: boolean },
      options?: Options,
    ) => {
      const remove = !!i.remove;
      const place = { day: i.day, timeZone: i.timeZone };
      const plans = plansOf(qc, [i.target]);
      write.mutate(
        {
          ...linkWrite(place, i.blockId, i.target, remove),
          record: untracked
            ? undefined
            : (r) =>
                r.history.push({
                  blocks: [i.blockId],
                  undo: async () => {
                    await r.run(
                      linkWrite(
                        place,
                        r.history.resolve(i.blockId),
                        i.target,
                        !remove,
                      ),
                    );
                    await r.restorePlans(plans);
                  },
                  redo: async () => {
                    await r.run(
                      linkWrite(
                        place,
                        r.history.resolve(i.blockId),
                        i.target,
                        remove,
                      ),
                    );
                  },
                }),
        },
        options,
      );
    },
  };
}
// Starting and ending cannot be taken back (an ended Block cannot be started
// again), so what was recorded about that Block is forgotten.
export function useStartEndBlock() {
  const write = useBlockWrite();
  return {
    ...write,
    mutate: (
      i: DayPlace & {
        block: TimeBlock;
        end: boolean;
        /** Takes back an end: the Block runs again, its time not cut. */
        resume?: boolean;
      },
      options?: { onError?(e: Error): void },
    ) =>
      write.mutate(
        {
          place: i,
          id: i.block.id,
          method: "POST",
          suffix: i.resume ? "/reopen" : i.end ? "/end" : "/start",
          patch: i.resume
            ? { endedAt: null }
            : i.end
              ? { endedAt: new Date().toISOString() }
              : { startedAt: new Date().toISOString(), endedAt: null },
          record: (r) => r.history.forget(i.block.id),
        },
        options,
      ),
  };
}
/**
 * A Case or Action taken off every Block, an Action also given another day (or
 * none): a card carried to "No date", or its "…" changing or taking off its
 * day. One Action does not stay on Blocks of a day it no longer has. Recorded
 * for ⌘Z unless it changed nothing.
 */
export function useUnassign() {
  const api = useApi(),
    qc = useQueryClient();
  const history = historyOf(qc);
  const unassign = (target: LinkTarget) =>
    api<{ blockIds: string[] }>(
      `/api/me/timeblocker/links?${new URLSearchParams(targetBody(target))}`,
      { method: "DELETE" },
    );
  const setPlan = (p: PlanSnapshot) =>
    api(`/api/me/cases/${encodeURIComponent(p.caseId)}`, {
      method: "PATCH",
      body: { update_steps: [{ id: p.stepId, plan: p.plan }] },
    }).finally(() => caseChanged(qc, p.caseId));
  const same = (a: ActionPatch["plan"], b: ActionPatch["plan"]) =>
    JSON.stringify(a) === JSON.stringify(b);
  type Input = DayPlace & {
    target: LinkTarget;
    /** The day it gets: none (default), "someday" or a day. */
    plan?: ActionPatch["plan"];
  };
  const mutation = useMutation({
    mutationFn: async (i: Input & { before: PlanSnapshot | undefined }) => {
      const { blockIds } = await unassign(i.target);
      const plan = i.plan ?? null;
      // The links are gone by now: a failure to change the day is not undone.
      const changes = !!i.target.stepId && !same(i.before?.plan ?? null, plan);
      let changed = false;
      let planError: Error | undefined;
      if (changes && i.target.stepId)
        changed = await setPlan({
          caseId: i.target.caseId,
          stepId: i.target.stepId,
          plan,
        }).then(
          () => true,
          (e: Error) => {
            planError = e;
            return false;
          },
        );
      return { blockIds, before: i.before, changed, planError };
    },
    onMutate: async (i: Input & { before: PlanSnapshot | undefined }) => {
      history.begin();
      await qc.cancelQueries({ queryKey: timeBlockerPrefix });
      const snapshot = qc.getQueriesData({ queryKey: dayPrefix });
      const { caseId, stepId } = i.target;
      qc.setQueriesData<TimeBlockerDay>({ queryKey: dayPrefix }, (old) =>
        old
          ? {
              ...old,
              blocks: old.blocks.map((b) => ({
                ...b,
                links: b.links.filter(
                  (l) =>
                    !(
                      l.caseId === caseId &&
                      (l.stepId ?? null) === (stepId ?? null)
                    ),
                ),
              })),
              actions: old.actions.map((a) =>
                stepId && a.id === stepId
                  ? ({ ...a, ...planFields(i.plan ?? null) } as CaseDayAction)
                  : a,
              ),
            }
          : old,
      );
      return { snapshot };
    },
    onSuccess: ({ blockIds, before, changed }, i) => {
      if (!blockIds.length && !changed) return;
      const place = { day: i.day, timeZone: i.timeZone };
      const plan = i.plan ?? null;
      history.push({
        blocks: blockIds,
        undo: async () => {
          for (const id of blockIds)
            await api(`${blockPath(history.resolve(id))}/links`, {
              method: "POST",
              body: { ...targetBody(i.target), plan_on: place.day },
              idempotencyKey: crypto.randomUUID(),
            });
          // Linking again sets the day; put the Action's own back (also "none").
          if (before) await setPlan(before);
        },
        redo: async () => {
          await unassign(i.target);
          if (before && changed) await setPlan({ ...before, plan });
        },
      });
    },
    onError: (_e, _i, ctx) => {
      for (const [key, value] of ctx?.snapshot ?? [])
        qc.setQueryData(key, value);
    },
    onSettled: () => {
      history.end();
    },
  });
  return {
    ...mutation,
    mutate: (i: Input, options?: { onError?(e: Error): void }) =>
      // The day as it is now, before the screen shows it changed.
      mutation.mutate(
        { ...i, before: plansOf(qc, [i.target])[0] },
        {
          onError: options?.onError,
          // The links are off already, but the day could not be changed.
          onSuccess: (done) => {
            if (done.planError) options?.onError?.(done.planError);
          },
        },
      ),
  };
}
/**
 * Working on a Case or Action inside a Block, started and stopped by hand
 * (several at once; starting one starts the Block when it was not started).
 * Not recorded for ⌘Z, like starting a Block.
 */
export function useBlockWork() {
  const write = useBlockWrite();
  return {
    ...write,
    start: (
      i: DayPlace & { block: TimeBlock; target: LinkTarget },
      options?: { onError?(e: Error): void },
    ) => {
      const at = new Date().toISOString();
      write.mutate(
        {
          place: i,
          id: i.block.id,
          method: "POST",
          suffix: "/works",
          body: targetBody(i.target),
          patch: {
            works: startedWorks(i.block.works, i.target, at),
            ...(i.block.startedAt ? {} : { startedAt: at, endedAt: null }),
          },
          record: i.block.startedAt
            ? undefined
            : (r) => r.history.forget(i.block.id),
        },
        options,
      );
    },
    stop: (
      i: DayPlace & { block: TimeBlock; target: LinkTarget },
      options?: { onError?(e: Error): void },
    ) =>
      write.mutate(
        {
          place: i,
          id: i.block.id,
          method: "POST",
          suffix: "/works/stop",
          body: targetBody(i.target),
          patch: {
            works: stoppedWorks(
              i.block.works,
              i.target,
              new Date().toISOString(),
            ),
          },
        },
        options,
      ),
  };
}
export interface ActionPatch {
  title?: string;
  status?: CaseStatus;
  plan?: { scope: "day" | "week"; on: string } | "someday" | null;
}
export function useUpdateAction() {
  const api = useApi(),
    qc = useQueryClient();
  return useMutation({
    mutationFn: (
      i: DayPlace & {
        action: Pick<CaseDayAction, "id" | "caseId">;
        patch: ActionPatch;
      },
    ) =>
      api(`/api/me/cases/${encodeURIComponent(i.action.caseId)}`, {
        method: "PATCH",
        body: { update_steps: [{ id: i.action.id, ...i.patch }] },
      }),
    onMutate: async (i) => {
      await qc.cancelQueries({ queryKey: timeBlockerPrefix });
      const snapshot = qc.getQueriesData({ queryKey: dayPrefix });
      qc.setQueriesData<TimeBlockerDay>({ queryKey: dayPrefix }, (old) =>
        old
          ? {
              ...old,
              actions: old.actions.map((a) =>
                a.id === i.action.id
                  ? ({
                      ...a,
                      ...i.patch,
                      ...(i.patch.plan !== undefined
                        ? planFields(i.patch.plan)
                        : {}),
                      closedAt:
                        i.patch.status === "done"
                          ? new Date().toISOString()
                          : a.closedAt,
                    } as CaseDayAction)
                  : a,
              ),
              blocks: old.blocks.map((b) => ({
                ...b,
                links: b.links.map((l) =>
                  l.stepId === i.action.id
                    ? {
                        ...l,
                        ...(i.patch.status ? { status: i.patch.status } : {}),
                        ...(i.patch.title ? { stepTitle: i.patch.title } : {}),
                      }
                    : l,
                ),
              })),
            }
          : old,
      );
      return { snapshot };
    },
    onError: (_e, _i, ctx) => {
      for (const [key, value] of ctx?.snapshot ?? [])
        qc.setQueryData(key, value);
    },
    onSettled: (_d, _e, i) => {
      caseChanged(qc, i.action.caseId);
    },
  });
}

// ---- events ----------------------------------------------------------------------

type EventsSnap = { key: QueryKey; prev: CalendarEvents | undefined };

async function patchEvents(
  qc: QueryClient,
  place: DayPlace,
  fn: (events: CalendarEvent[]) => CalendarEvent[],
): Promise<EventsSnap> {
  const key = eventsKey(place.day, place.timeZone);
  await qc.cancelQueries({ queryKey: key });
  const prev = qc.getQueryData<CalendarEvents>(key);
  if (prev) qc.setQueryData(key, { ...prev, events: fn(prev.events) });
  return { key, prev };
}

const restoreEvents = (qc: QueryClient, snap: EventsSnap | undefined) => {
  if (snap?.prev) qc.setQueryData(snap.key, snap.prev);
};

const sameEvent = (a: CalendarEvent, b: { id: string; sourceId: string }) =>
  a.id === b.id && a.sourceId === b.sourceId;

const eventPath = (e: { id: string; sourceId: string }) =>
  `/api/me/calendar/events/${encodeURIComponent(e.sourceId)}/${encodeURIComponent(e.id)}`;

/** Whether an event is being created (no Google id yet): it cannot be moved. */
export { isOptimisticId as isPendingId } from "../data/optimistic";

export interface CreateEventInput extends DayPlace {
  /** The lane the new event shows in at once (null: the own lane). */
  laneId: string | null;
  sourceId: string;
  title: string;
  times: EventTimesBody;
}

export function useCreateEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventInput) =>
      api<CalendarEvent>("/api/me/calendar/events", {
        method: "POST",
        body: { source_id: input.sourceId, title: input.title, ...input.times },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onMutate: async (input) => {
      const draft = applyEventBody(
        {
          id: newOptimisticId(),
          sourceId: input.sourceId,
          laneId: input.laneId,
          title: input.title,
          startAt: null,
          endAt: null,
          startDate: null,
          endDate: null,
          location: null,
          description: null,
          meetingUrl: null,
          htmlLink: null,
          etag: null,
          recurring: false,
        },
        input.times,
      );
      return { snap: await patchEvents(qc, input, (es) => [...es, draft]) };
    },
    onError: (_e, _input, ctx) => restoreEvents(qc, ctx?.snap),
    onSettled: () => qc.invalidateQueries({ queryKey: eventsPrefix }),
  });
}

export interface UpdateEventInput extends DayPlace {
  event: CalendarEvent;
  body: EventTimesBody & { title?: string };
}

export function useUpdateEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // The etag read is sent along: a change on Google's side since is a 409.
    mutationFn: (input: UpdateEventInput) =>
      api<CalendarEvent>(eventPath(input.event), {
        method: "PATCH",
        body: {
          ...(input.event.etag ? { etag: input.event.etag } : {}),
          ...input.body,
        },
      }),
    onMutate: async (input) => ({
      snap: await patchEvents(qc, input, (es) =>
        es.map((e) =>
          sameEvent(e, input.event) ? applyEventBody(e, input.body) : e,
        ),
      ),
    }),
    // The new etag at once; each copy keeps its lane.
    onSuccess: (dto, input) => {
      const key = eventsKey(input.day, input.timeZone);
      const prev = qc.getQueryData<CalendarEvents>(key);
      if (prev)
        qc.setQueryData(key, {
          ...prev,
          events: prev.events.map((e) =>
            sameEvent(e, input.event) ? { ...dto, laneId: e.laneId } : e,
          ),
        });
    },
    onError: (_e, _input, ctx) => restoreEvents(qc, ctx?.snap),
    onSettled: () => qc.invalidateQueries({ queryKey: eventsPrefix }),
  });
}

export function useDeleteEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DayPlace & { event: CalendarEvent }) =>
      api(
        `${eventPath(input.event)}${
          input.event.etag
            ? `?etag=${encodeURIComponent(input.event.etag)}`
            : ""
        }`,
        { method: "DELETE" },
      ),
    onMutate: async (input) => ({
      snap: await patchEvents(qc, input, (es) =>
        es.filter((e) => !sameEvent(e, input.event)),
      ),
    }),
    onError: (_e, _input, ctx) => restoreEvents(qc, ctx?.snap),
    onSettled: () => qc.invalidateQueries({ queryKey: eventsPrefix }),
  });
}

// ---- calendar settings -----------------------------------------------------------

/** A settings write: waits for the answer, then reads the settings and events again. */
function useCalendarWrite<V, R>(fn: (api: Api, v: V) => Promise<R>) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(api, v),
    onSettled: () => calendarChanged(qc),
  });
}

export const useUpdateSource = () =>
  useCalendarWrite(
    (
      api,
      v: {
        source: CalendarSource;
        body: {
          lane_id?: string | null;
          shown?: boolean;
          color?: string | null;
        };
      },
    ) =>
      api<CalendarSource>(
        `/api/me/calendar/sources/${encodeURIComponent(v.source.id)}`,
        { method: "PATCH", body: v.body },
      ),
  );

export const useCreateLane = () =>
  useCalendarWrite((api, name: string) =>
    api<CalendarLane>("/api/me/calendar/lanes", {
      method: "POST",
      body: { name },
    }),
  );

export const useRenameLane = () =>
  useCalendarWrite((api, v: { lane: CalendarLane; name: string }) =>
    api<CalendarLane>(
      `/api/me/calendar/lanes/${encodeURIComponent(v.lane.id)}`,
      {
        method: "PATCH",
        body: { name: v.name },
      },
    ),
  );

export const useDeleteLane = () =>
  useCalendarWrite((api, lane: CalendarLane) =>
    api(`/api/me/calendar/lanes/${encodeURIComponent(lane.id)}`, {
      method: "DELETE",
    }),
  );

export const useSyncCalendars = () =>
  useCalendarWrite((api, _v: undefined) =>
    api<CalendarSettings>("/api/me/calendar/sync", {
      method: "POST",
      body: {},
    }),
  );

export const useDisconnectGoogle = () =>
  useCalendarWrite((api, c: GoogleConnection) =>
    api(`/api/me/google/connections/${encodeURIComponent(c.id)}`, {
      method: "DELETE",
    }),
  );

/**
 * Starts connecting a Google account: the server makes the consent screen's
 * address, and this window goes there; it comes back to `returnTo`. Only a
 * failure returns (a refused start).
 */
export async function startGoogleConnect(
  api: Api,
  options: { connectionId?: string; returnTo: string },
): Promise<void> {
  const { url } = await api<{ url: string }>("/api/me/google/connect", {
    method: "POST",
    body: {
      ...(options.connectionId ? { connection_id: options.connectionId } : {}),
      return_to: options.returnTo,
    },
  });
  window.location.assign(url);
}
